import { useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import addUserIcon from "../../../../assets/users/user_add.svg";
import { useManageUsersContext } from "../../../../context/ManageUsersContext";
import useResponsive from "../../../../hooks/useResponsive";
import { Button } from "@shltelemedicine/shl-ui-lib";
import SearchAndFilter from "../../../Shared/SearchAndFilter/SearchAndFilter";
import Paging from "../../../Shared/Table/Paging/Paging";
import ManagementHeader, {
  ManagementHeaderDivider,
} from "../../ManagementHeader/ManagementHeader";
import UsersCount from "../../ManagementHeader/UsersCount/UsersCount";
import styles from "./ManageUsersHeader.module.css";

type ManageUsersHeaderProps = {
  itemsPerPage?: number;
  onAddUser?: () => void;
  onToggleFilter?: () => void;
};

const ManageUsersHeader = ({
  itemsPerPage = 30,
  onAddUser,
  onToggleFilter,
}: ManageUsersHeaderProps) => {
  const {
    state: { users, usersTableRef, loading },
  } = useManageUsersContext();

  const [search, setSearch] = useState(usersTableRef?.globalFilter);

  const { isTabletPortrait } = useResponsive();

  const searchChangeHandler = (text: string) => {
    setSearch(text);
    usersTableRef?.setGlobalFilter(text || undefined);
  };

  const addUserHandler = () => {
    if (onAddUser) onAddUser();
  };

  const toggleFilterHandler = () => {
    if (onToggleFilter) onToggleFilter();
  };

  const clearSearchHandler = () => {
    setSearch(null);
    usersTableRef?.setGlobalFilter(undefined);
  };

  const totalUsers = users?.length;

  const activeUsers = users.length
    ? users.filter((u) => u.status === "active").length
    : undefined;

  const pendingUsers = users.length
    ? users.filter((u) => u.status === "pending").length
    : undefined;

  const inactiveUsers = users.length
    ? users.filter((u) => u.status === "disabled").length
    : undefined;

  return (
    <ManagementHeader className="justify-content-between py-1 pl-2 z-index-front">
      {!isTabletPortrait ? (
        <>
          <SearchAndFilter
            searchText={search}
            onSearchTextChange={searchChangeHandler}
            toggleFilter={toggleFilterHandler}
            onClearSearch={clearSearchHandler}
            showFilterBtn={false}
            disabled={loading}
          />
          <div className="d-flex align-items-center">
            <Button
              variant=""
              className={styles.button}
              icon={{ src: addUserIcon, width: 18, height: 18 }}
              onClick={addUserHandler}
              disabled={loading}
            >
              <FormattedMessage
                id="MANAGEMENT.ADD_USER"
                defaultMessage="Add User"
              />
            </Button>
            <ManagementHeaderDivider className="my-2" />
            <UsersCount
              active={activeUsers}
              total={totalUsers}
              inactive={inactiveUsers}
              pending={pendingUsers}
              extended
              showPending
            />
            <ManagementHeaderDivider className="my-2" />
            <div className="d-flex align-items-center">
              <Paging
                total={users?.length}
                onNext={usersTableRef?.onNextPage}
                onBack={usersTableRef?.onPreviousPage}
                nextDisabled={usersTableRef?.nextDisabled ?? true}
                backDisabled={usersTableRef?.backDisabled ?? true}
                curPage={usersTableRef?.curPage ?? 0}
                itemsPerPage={itemsPerPage}
              />
            </div>
          </div>
        </>
      ) : (
        <Container>
          <Row className="align-items-center my-2">
            <Col xs={6}>
              <SearchAndFilter
                searchText={search}
                onSearchTextChange={searchChangeHandler}
                toggleFilter={toggleFilterHandler}
                onClearSearch={clearSearchHandler}
                showFilterBtn={false}
                disabled={loading}
              />
            </Col>
            <Col xs={2}>
              <Button
                variant=""
                className={styles.button}
                icon={{ src: addUserIcon, width: 18, height: 18 }}
                onClick={addUserHandler}
                disabled={loading}
              ></Button>
            </Col>
            <Col xs={4}>
              <Paging
                total={users?.length}
                onNext={usersTableRef?.onNextPage}
                onBack={usersTableRef?.onPreviousPage}
                nextDisabled={usersTableRef?.nextDisabled ?? true}
                backDisabled={usersTableRef?.backDisabled ?? true}
                curPage={usersTableRef?.curPage ?? 0}
                itemsPerPage={itemsPerPage}
              />
            </Col>
          </Row>
          <Row className="align-items-center my-3">
            <Col>
              <UsersCount
                active={activeUsers}
                total={totalUsers}
                inactive={inactiveUsers}
                pending={pendingUsers}
                extended
                showPending
              />
            </Col>
          </Row>
        </Container>
      )}
    </ManagementHeader>
  );
};

export default ManageUsersHeader;
